import {
  ItemTypeEnum,
  ProductTypeEnum,
  abutmentCharacteristicsSchema,
  implantBridgeSchema,
  implantCharacteristicsSchema,
  implantedItemCharacteristicsSchema,
  isToothScopedItem,
  materialCharacteristicsSchema,
  productPreferencesSchema,
  type AbutmentItem,
  type BridgePonticItem,
  type DeepPartial,
  type ImplantBridgeItem,
  type ImplantBridgeProduct,
} from "@jasper/client-models";
import {
  isTeethStepComplete,
  TeethSelectionEnum,
  type ProductWorkflowConfiguration,
} from "./ProductWorkflowConfiguration";
import { StepKeysEnum } from "../../steps/types";
import {
  AbutmentStep,
  ImplantBridgeTeethStep,
  ImplantConnectionStep,
  ImplantRetentionStep,
  MaterialStep,
  PreferencesStep,
} from "./constants";

export type ImplantBridgeStepKey =
  | typeof StepKeysEnum.implantBridgeTeeth
  | typeof StepKeysEnum.implantConnection
  | typeof StepKeysEnum.abutment
  | typeof StepKeysEnum.implantRetention
  | typeof StepKeysEnum.material
  | typeof StepKeysEnum.preferences;

const isAbutment = (
  item: DeepPartial<ImplantBridgeItem | AbutmentItem | BridgePonticItem> = {}
): item is DeepPartial<AbutmentItem> =>
  item.type === ItemTypeEnum.abutment ||
  item.type === ItemTypeEnum.customAbutment;

const isImplantBridge = (
  item: DeepPartial<ImplantBridgeItem | AbutmentItem | BridgePonticItem> = {}
): item is DeepPartial<ImplantBridgeItem> =>
  item.type === ItemTypeEnum.bridgeImplant;

const isBridgePontic = (
  item: DeepPartial<ImplantBridgeItem | AbutmentItem | BridgePonticItem> = {}
): item is DeepPartial<BridgePonticItem> =>
  item.type === ItemTypeEnum.bridgePontic;

export const ImplantBridgeWorkflowConfiguration: ProductWorkflowConfiguration<
  ImplantBridgeProduct,
  ImplantBridgeStepKey
> = {
  productType: ProductTypeEnum.bridgeImplant,
  itemType: ItemTypeEnum.bridgeImplant,
  steps: [
    new ImplantBridgeTeethStep(),
    new ImplantConnectionStep(),
    new AbutmentStep(),
    new ImplantRetentionStep(),
    new MaterialStep({
      props: {
        materialItemTypes: [
          ItemTypeEnum.bridgeImplant,
          ItemTypeEnum.bridgePontic,
        ],
        formatProductSelection: ({
          product,
          selectedItemsMaterialCharacteristics,
        }) => {
          return {
            productId: product.id,
            items: product.items.map(item => {
              const matchingItem =
                isToothScopedItem(item) &&
                (item.type === ItemTypeEnum.bridgeImplant ||
                  item.type === ItemTypeEnum.bridgePontic)
                  ? selectedItemsMaterialCharacteristics.find(
                      i => i.tooth === item.tooth
                    )
                  : undefined;

              return {
                ...item,
                materialCharacteristics:
                  matchingItem?.materialCharacteristics ??
                  item.materialCharacteristics,
              };
            }),
          };
        },
      },
    }),
    new PreferencesStep(),
  ],
  teethSelection: TeethSelectionEnum.ADJACENT,
  isBulkSupported: true,
  bulkCompatibleTypes: [ProductTypeEnum.bridgeImplant],

  isValidProduct(product) {
    return implantBridgeSchema.safeParse(product).success;
  },

  getCompletedSteps(product) {
    const completed: ImplantBridgeStepKey[] = [];

    if (
      isTeethStepComplete({
        teeth: product.teeth,
        teethSelection: this.teethSelection,
      })
    ) {
      completed.push(StepKeysEnum.implantBridgeTeeth);
    }

    const hasAbutment = product.items?.some(item => isAbutment(item));

    if (hasAbutment) {
      if (
        product.items?.every(
          item =>
            isImplantBridge(item) ||
            isBridgePontic(item) ||
            implantCharacteristicsSchema.safeParse(item.implantCharacteristics)
              .success
        )
      ) {
        completed.push(StepKeysEnum.implantConnection);
      }

      if (
        product.items?.every(
          item =>
            isImplantBridge(item) ||
            isBridgePontic(item) ||
            abutmentCharacteristicsSchema.safeParse(
              item.abutmentCharacteristics
            ).success
        )
      ) {
        completed.push(StepKeysEnum.abutment);
      }
    }

    const hasBridges = product.items?.some(
      item => isImplantBridge(item) || isBridgePontic(item)
    );

    if (hasBridges) {
      if (
        product.items?.every(
          item =>
            isAbutment(item) ||
            isBridgePontic(item) ||
            implantedItemCharacteristicsSchema.safeParse(
              item.implantedItemCharacteristics
            ).success
        )
      ) {
        completed.push(StepKeysEnum.implantRetention);
      }

      if (
        product.items?.every(
          item =>
            isAbutment(item) ||
            materialCharacteristicsSchema.safeParse(
              item.materialCharacteristics
            ).success
        )
      ) {
        completed.push(StepKeysEnum.material);
      }
    }

    if (productPreferencesSchema.safeParse(product).success) {
      completed.push(StepKeysEnum.preferences);
    }

    return completed;
  },
};
